/**
 * Pure cascade-reorder algorithm for the recipe's flat preparation-step
 * sequence. A node's stepOrder must always stay consistent with the
 * dependency DAG — strictly less than every one of its parents' and
 * greater than every one of its children's (children are dependencies/
 * inputs, prepared first; parents are consumers, prepared later) — so
 * dragging a node to a new position can't just overwrite its own index:
 * if the move would place it on the wrong side of a dependency, that
 * other node has to move too, and that can cascade further (pulling a
 * grandchild along when a child moves, and so on).
 *
 * Deliberately independent of RecipeStore/DisplayNode — operates on
 * plain ids plus two adjacency callbacks, so it's easy to verify
 * standalone (small hand-built DAGs, no store/DOM involved) and reusable
 * from both the Elaboració and Timeline drag UIs via one shared
 * RecipeStore method (moveNodeToStepOrder()).
 */

export interface StepOrderGraph {
  /** Every participating node id, already sorted by its CURRENT stepOrder. */
  orderedIds: string[];
  /** A node's own dependencies (inputs) — RecipeNode.children's meaning. */
  childrenOf(id: string): string[];
  /** Every node that lists `id` as one of its own children. */
  parentsOf(id: string): string[];
}

/**
 * Move `movedId` so it ends up at `targetIndex` in the final flat order
 * (clamped to a valid index — pass orderedIds.length - 1 or anything
 * past it to mean "move to the very end"), cascading whichever other
 * nodes a dependency constraint forces to move too. Only one direction
 * ever needs checking: moving a node earlier can only ever violate its
 * OWN children's required "before me" position (its parents were already
 * positioned after it, and moving further away from them can't break
 * that); moving it later is the mirror case, checking only its parents.
 *
 * Returns a fresh, dense 0..N-1 stepOrder map for every id in
 * `graph.orderedIds` (including ones that didn't conceptually move at
 * all) — callers write this wholesale rather than patching individual
 * nodes, since inserting one node ahead of another shifts everyone
 * between them by one regardless of whether they were "involved".
 */
export function reorderWithCascade(
  graph: StepOrderGraph,
  movedId: string,
  targetIndex: number,
): Map<string, number> {
  const { orderedIds, childrenOf, parentsOf } = graph;
  const n = orderedIds.length;
  const originalIndex = orderedIds.indexOf(movedId);
  if (originalIndex === -1) {
    throw new Error(`reorderWithCascade: movedId "${movedId}" is not in orderedIds`);
  }

  const clampedTarget = Math.max(0, Math.min(targetIndex, n - 1));
  const moveEarlier = clampedTarget < originalIndex;

  const ids = orderedIds.slice();
  ids.splice(originalIndex, 1);
  const insertAt = Math.max(0, Math.min(clampedTarget, ids.length));
  ids.splice(insertAt, 0, movedId);

  if (moveEarlier) {
    cascadeEarlier(ids, movedId, childrenOf);
  } else {
    cascadeLater(ids, movedId, parentsOf);
  }

  const result = new Map<string, number>();
  ids.forEach((id, index) => result.set(id, index));
  return result;
}

/**
 * `anchorId` just moved earlier. Any of its children positioned at/after
 * its new slot are a dependency violation (a child must stay before its
 * parent) — pull each one to sit immediately before the anchor,
 * preserving everyone else's relative order, and recurse: a pulled
 * child becomes the next anchor, since pulling IT earlier can just as
 * easily violate its own children's position in turn.
 */
function cascadeEarlier(ids: string[], anchorId: string, childrenOf: (id: string) => string[]): void {
  const queue = [anchorId];
  const queued = new Set(queue);
  while (queue.length > 0) {
    const current = queue.shift()!;
    let anchorIdx = ids.indexOf(current);
    for (const childId of childrenOf(current)) {
      if (childId === current) continue;
      const childIdx = ids.indexOf(childId);
      if (childIdx === -1 || childIdx <= anchorIdx) continue; // not a violation (or not in this set)
      ids.splice(childIdx, 1);
      ids.splice(anchorIdx, 0, childId);
      anchorIdx += 1; // current shifted forward by one slot
      if (!queued.has(childId)) { queue.push(childId); queued.add(childId); }
    }
  }
}

/** Mirror of cascadeEarlier() — `anchorId` moved later; pull any parent
 *  now positioned before it (a violation: a parent must stay after its
 *  own dependency) to sit immediately after, recursing the same way. */
function cascadeLater(ids: string[], anchorId: string, parentsOf: (id: string) => string[]): void {
  const queue = [anchorId];
  const queued = new Set(queue);
  while (queue.length > 0) {
    const current = queue.shift()!;
    let anchorIdx = ids.indexOf(current);
    for (const parentId of parentsOf(current)) {
      if (parentId === current) continue;
      const parentIdx = ids.indexOf(parentId);
      if (parentIdx === -1 || parentIdx >= anchorIdx) continue; // not a violation (or not in this set)
      ids.splice(parentIdx, 1);
      anchorIdx -= 1; // current shifted back by one slot
      ids.splice(anchorIdx + 1, 0, parentId);
      if (!queued.has(parentId)) { queue.push(parentId); queued.add(parentId); }
    }
  }
}
